import { useEffect, useState } from 'react';
import type { ShipmentPending } from '@kassa/shared';
import { api, ApiError } from '../api';
import { formatDate } from '../format';
import { FreeIssue } from './FreeIssue';
import { useConfirm } from '../components/ConfirmDialog';
import type { IssuedShipment, ShipmentWarehouse } from '../api/shipments';

interface Props {
  onBack: () => void;
}

// Anbardar: rezervdəki şirkət satışlarından malın verilməsi (KT-60). Qiymət və pul göstərilmir.
// Qismən də verilə bilər; qalan miqdar rezervdə qalır. Eyni «Ver» basışı eyni UUID ilə gedir.
export function Shipments({ onBack }: Props) {
  const confirmDialog = useConfirm();
  const [issued, setIssued] = useState<IssuedShipment[]>([]);
  const [warehouses, setWarehouses] = useState<ShipmentWarehouse[]>([]);
  const [pending, setPending] = useState<ShipmentPending[] | null>(null);
  const [qtys, setQtys] = useState<Record<string, string>>({});
  const [ids, setIds] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function loadSettings() {
    api.shipmentsIssued().then(setIssued).catch(() => setIssued([]));
    api.shipmentWarehouses().then(setWarehouses).catch(() => setWarehouses([]));
  }

  async function toggleTwoStep(w: ShipmentWarehouse) {
    if (!(await confirmDialog(`«${w.name}» üçün iki addımlı rejim ${w.twoStep ? 'bağlansın' : 'açılsın'}?`, { confirmLabel: 'Təsdiq et' }))) return;
    try {
      await api.shipmentSetTwoStep(w.id, !w.twoStep);
      loadSettings();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Rejim dəyişmədi');
    }
  }

  async function cancelIssue(row: IssuedShipment) {
    if (!(await confirmDialog(`«${row.displayNo}» verilməsi ləğv olunsun? Mal qalığa geri qayıdacaq, sənəd silinmir.`, { confirmLabel: 'Ləğv et', danger: true }))) return;
    try {
      await api.shipmentCancel(row.id, crypto.randomUUID());
      setNotice(`${row.displayNo} ləğv olundu`);
      loadSettings();
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Ləğv olunmadı');
    }
  }

  function reload() {
    return api
      .shipmentsPending()
      .then((p) => {
        setPending(p);
        setQtys(Object.fromEntries(p.flatMap((d) => d.lines.map((l) => [l.lineId, String(l.remaining)]))));
        setError(null);
      })
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Siyahı yüklənmədi'));
  }

  useEffect(() => {
    reload();
    loadSettings();
  }, []);

  async function issue(doc: ShipmentPending) {
    const lines = doc.lines
      .map((l) => ({ lineId: l.lineId, qty: Number(qtys[l.lineId]) }))
      .filter((l) => l.qty > 0);
    if (lines.length === 0) return setError('Verilən miqdarı daxil edin');
    const bad = doc.lines.find((l) => Number(qtys[l.lineId]) > l.remaining);
    if (bad) return setError(`«${bad.productName}»: ${bad.remaining} ədəddən çox vermək olmaz`);
    const id = ids[doc.docId] ?? crypto.randomUUID();
    setIds((cur) => ({ ...cur, [doc.docId]: id }));
    setBusy(true);
    setError(null);
    try {
      const r = await api.shipmentIssue(doc.docId, { id, lines });
      setNotice(`${doc.displayNo}: ${r.displayNo} verildi`);
      setIds((cur) => {
        const next = { ...cur };
        delete next[doc.docId];
        return next;
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Verilmə alınmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="shell">
      <header className="topbar">
        <span className="brand">Verilməli mallar</span>
        <span className="topbar-spacer" />
        <button type="button" className="link-btn back-home" onClick={onBack}>
          Geri
        </button>
      </header>
      <main className="content">
        <section className="card card-wide">
          <h2>Verilməli mallar</h2>
          <p className="muted small">Şirkət satışları rezervdədir. Malı verdikdə «Ver» basın — qismən də ola bilər; qalan rezervdə qalır.</p>
          {notice && <p className="ok" aria-live="polite">{notice}</p>}
          {error && (
            <p className="bad" role="alert">
              {error}
            </p>
          )}
          {!pending && !error && <p className="muted">Yüklənir…</p>}
          {pending && pending.length === 0 && <p className="muted">Verilməli mal yoxdur.</p>}
          {pending &&
            pending.map((doc) => (
              <div key={doc.docId} className="card">
                <p>
                  <strong>{doc.customerName}</strong> · {doc.displayNo} · {formatDate(doc.date)}
                </p>
                <ul className="product-list">
                  {doc.lines.map((l) => (
                    <li key={l.lineId} className="product-row">
                      <span className="product-name">{l.productName}</span>
                      <span className="muted small">qalan {l.remaining} {l.unit}</span>
                      <input
                        type="number"
                        min="0"
                        max={l.remaining}
                        step="0.001"
                        aria-label={`${l.productName} verilən miqdar`}
                        value={qtys[l.lineId] ?? ''}
                        onChange={(e) => setQtys((cur) => ({ ...cur, [l.lineId]: e.target.value }))}
                        disabled={busy}
                      />
                    </li>
                  ))}
                </ul>
                <button type="button" className="btn-primary" onClick={() => issue(doc)} disabled={busy}>
                  Ver
                </button>
              </div>
            ))}
          <section className="card">
            <h3>Anbar rejimi</h3>
            <ul className="product-list">
              {warehouses.map((w) => (
                <li key={w.id} className="product-row">
                  <span className="product-name">{w.outletName} · {w.name}</span>
                  <span className="muted small">{w.twoStep ? 'iki addımlı' : 'bir addımlı'}</span>
                  <button type="button" className="btn-small" onClick={() => toggleTwoStep(w)}>
                    {w.twoStep ? 'Bağla' : 'Aç'}
                  </button>
                </li>
              ))}
            </ul>
          </section>
          <section className="card">
            <h3>Verilmiş mallar</h3>
            {issued.length === 0 && <p className="muted">Verilmə yoxdur.</p>}
            <ul className="product-list">
              {issued.map((row) => (
                <li key={row.id} className="product-row">
                  <span className="product-name">{row.customerName}</span>
                  <span className="muted small">{row.displayNo} · {row.date} · {row.qty}</span>
                  <button type="button" className="link-btn" onClick={() => cancelIssue(row)}>
                    Ləğv et
                  </button>
                </li>
              ))}
            </ul>
          </section>
          <FreeIssue
            onDone={(msg) => {
              setNotice(msg);
              reload();
            }}
          />
        </section>
      </main>
    </div>
  );
}
